// Common English filler/stop words (trim as you like)
const DEFAULT_STOPWORDS = new Set([
    'a','an','the',
    'and','or','but','so','if','then','else','than','that','this','these','those',
    'i','you','he','she','it','we','they','me','him','her','us','them',
    'my','your','his','her','its','our','their','yours','ours','theirs',
    'is','am','are','was','were','be','been','being',
    'have','has','had','do','does','did','doing',
    'will','would','shall','should','can','could','may','might','must',
    'of','at','by','for','with','about','against','between','into','through',
    'during','before','after','to','from','in','out','on','off','over','under',
    'as','up','down','again','further','once','just','only','also','very',
    'no','nor','not','too','s', 't'
]);

/**
 * Remove common filler words.
 * @param words Array of tokens (e.g., from a split)
 * @param stopwords Optional custom set to use/extend
 */
export const filterFillerWords = (
    words: string[],
    stopwords: Set<string> = DEFAULT_STOPWORDS
): string[] => {
    return words
        // normalize each token: strip edge punctuation and lowercase
        .map(w => w.replace(/^[-–—'"([{<.,:;!?]+|[-–—'")\]}>.,:;!?]+$/g, '').toLowerCase())
        // drop empties and single-letter leftovers (except 'a'/'i' which are in stopwords anyway)
        .filter(w => w.length > 1)
        // keep tokens not in the stop list
        .filter(w => !stopwords.has(w));
};

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Generate a regex for word-based search.
 *
 * @param filterString The input text used for filtering.
 * @param options Optional configuration:
 *   - ignoreFillerWords: whether to skip stopword filtering (default: true)
 *   - customStopwords: optional custom stopword set
 *
 * @returns RegExp used to match input against a target string
 */
export const generateWordSearchRegex = (
    filterString: string,
    options?: {
        ignoreFillerWords?: boolean;
        customStopwords?: Set<string>;
    }
): RegExp => {
    const { ignoreFillerWords = true, customStopwords } = options ?? {};

    const words = filterString.trim().split(/\s+/);
    const tokens = ignoreFillerWords
        ? filterFillerWords(words, customStopwords ?? DEFAULT_STOPWORDS)
        : words.map(w => w.replace(/^[-–—'"([{<.,:;!?]+|[-–—'")\]}>.,:;!?]+$/g, '').toLowerCase());

    const cleaned = tokens.map(escapeRe);

    if (cleaned.length === 0) return new RegExp('(?!)'); // match-nothing guard
    return new RegExp(`(?:${cleaned.join('|')})`, 'i'); // non-capturing group
};
